/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

'use client';

import React from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';
import ContentStateView from '@airasia-phoenix/react-native/widgets/ContentStateView';
import {
  ButtonViewState,
  ButtonViewType,
} from '@airasia-phoenix/react-native/components/ButtonView/types';
import { styles } from './NotFoundPage.styles';

const NOT_FOUND_ILLUSTRATION_URL =
  'https://static.airasia.com/design/illustration/failed-search.svg';

export interface NotFoundPageProps {
  onGoToHomepage: () => void;
  /** Custom illustration URL. Falls back to the default not-found illustration. */
  imageUrl?: string;
  /** Custom primary text. Falls back to the default title. */
  primaryText?: string;
  /** Custom secondary text. Falls back to the default subtitle. */
  secondaryText?: string;
  /** Custom button text. Falls back to the default "Go to homepage". */
  buttonText?: string;
}

const NotFoundPage: React.FC<NotFoundPageProps> = ({
  onGoToHomepage,
  imageUrl,
  primaryText,
  secondaryText,
  buttonText,
}) => {
  const { t } = useTranslation('commonWeb');

  return (
    <View style={styles.container} testID="not-found-page">
      <ContentStateView
        imageViewProps={{
          url: imageUrl ?? NOT_FOUND_ILLUSTRATION_URL,
          height: 160,
          width: 182,
        }}
        primaryText={
          primaryText ??
          t('notFoundPage.title', 'We can’t find what you’re looking for')
        }
        secondaryText={
          secondaryText ??
          t('notFoundPage.subtitle', 'Let’s get you back to the homepage.')
        }
        buttonViewProps={{
          type: ButtonViewType.SECONDARY_SMALL,
          text: buttonText ?? t('notFoundPage.goToHomepage', 'Go to homepage'),
          onPress: onGoToHomepage,
          disabled: false,
          buttonViewState: ButtonViewState.LOADED,
        }}
      />
    </View>
  );
};

export default NotFoundPage;
